import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '../stores/authStore';
import keycloak from '../config/keycloak';
import { App, Template, HealthResponse, InfoResponse, LandingTemplate, LandingFile, StaticSite, CreateStaticSitePayload, DeployBulkResult, DeleteBulkResult, AppDomainFile } from '../types/api';
import { EndpointMapping, TemplateDynamicFields } from '../types/index';
const API_BASE_URL = import.meta.env.VITE_API_URL || 
  (window.location.origin.includes('localhost') ? 'http://localhost:3000' : '/api');

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 30000, // 30 секунд timeout для великих запитів
    });

    // Request interceptor для додавання токена та логування
    this.api.interceptors.request.use(
      async (config) => {
        if (keycloak.authenticated) {
          try {
            // Оновлюємо токен якщо до закінчення залишилось менше 30 секунд
            const refreshed = await keycloak.updateToken(30);
            
            if (refreshed && keycloak.token) {
              // Синхронізуємо токен в localStorage та authStore
              localStorage.setItem("accessToken", keycloak.token);
              const currentState = useAuthStore.getState();
              if (currentState.user) {
                currentState.login({
                  access_token: keycloak.token,
                  user: currentState.user,
                });
              }
            }
            
            config.headers.Authorization = `Bearer ${keycloak.token}`;
          } catch (error) {
            useAuthStore.getState().logout();
            if (keycloak.authenticated) {
              keycloak.logout({ redirectUri: window.location.origin });
            }
            return Promise.reject(error);
          }
        }

        console.log(
          `🔄 API Request: ${config.method?.toUpperCase()} ${config.url}`,
          {
            data: config.data,
            params: config.params,
          }
        );

        return config;
      },
      (error) => {
        console.error("❌ API Request Error:", error);
        return Promise.reject(error);
      }
    );

    // Response interceptor для обробки помилок авторизації та логування
    this.api.interceptors.response.use(
      (response) => {
        console.log(
          `✅ API Response: ${response.config.method?.toUpperCase()} ${
            response.config.url
          }`,
          {
            status: response.status,
            data: response.data,
          }
        );
        return response;
      },
      (error: AxiosError) => {
        // Логування помилок
        console.error("❌ API Error:", {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          message: error.message,
          data: error.response?.data,
        });

        if (error.response?.status === 401) {
          // Якщо 401, то очищуємо auth state та виходимо з Keycloak
          console.warn("🔐 Unauthorized - clearing auth state");
          useAuthStore.getState().logout();
          
          if (keycloak.authenticated) {
            keycloak.logout({
              redirectUri: window.location.origin,
            });
          }
        }

        // Покращуємо повідомлення про помилку
        const errorMessage = this.getErrorMessage(error);
        const enhancedError = new Error(errorMessage);
        (enhancedError as any).originalError = error;

        return Promise.reject(enhancedError);
      }
    );
  }

  private getErrorMessage(error: AxiosError): string {
    if (error.response?.data) {
      const data = error.response.data as any;
      if (data.message) return data.message;
      if (data.error) return data.error;
      if (typeof data === "string") return data;
    }

    if (error.code === "ECONNABORTED") {
      return "Час очікування запиту вичерпано";
    }

    if (error.code === "ERR_NETWORK") {
      return "Помилка мережі. Перевірте з'єднання з інтернетом";
    }

    return error.message || "Невідома помилка";
  }

  // System endpoints
  async getHealth(): Promise<HealthResponse> {
    const response = await this.api.get("/health");
    return response.data;
  }

  async getInfo(): Promise<InfoResponse> {
    const response = await this.api.get("/");
    return response.data;
  }

  // Auth methods
  // Методи login та register видалено - тепер використовується Keycloak
  // Логін та реєстрація виконуються через Keycloak

  async getProfile() {
    const response = await this.api.get("/auth/profile");
    return response.data;
  }

  async updateProfile(data: any) {
    const response = await this.api.put("/auth/profile", data);
    return response.data;
  }

  // Метод changePassword видалено - паролі керуються через Keycloak


  /** @param refresh — якщо true, бекенд оновить кеш дозволених апок з CRM. Повертає { apps, allowedAppIds }. */
  async getApps(params?: { refresh?: boolean }): Promise<{ apps: App[]; allowedAppIds: string[] }> {
    const response = await this.api.get("/apps", {
      params: params?.refresh ? { refresh: "true" } : undefined,
    });
    const data = response.data;
    return {
      apps: data?.apps ?? (Array.isArray(data) ? data : []),
      allowedAppIds: Array.isArray(data?.allowedAppIds) ? data.allowedAppIds : [],
    };
  }

  async getAppsWithOutdatedTemplates(): Promise<any[]> {
    const response = await this.api.get("/apps/outdated-templates");
    return response.data?.apps || [];
  }

  async getApp(id: string): Promise<App> {
    const response = await this.api.get(`/apps/${id}`);
    return response.data;
  }

  async createApp(data: Partial<App>): Promise<App> {
    const response = await this.api.post("/apps", data);
    return response.data;
  }

  async updateApp(id: string, data: Partial<App>): Promise<App> {
    const response = await this.api.patch(`/apps/${id}`, data);
    return response.data;
  }

  async updateAppParameters(
    id: string,
    data: {
      allowInt?: boolean;
      debugMode?: boolean;
      whiteJson?: string;
      grayJson?: string;
    },
  ): Promise<{
    success: boolean;
    changes: Array<{ field: string; before: string | null; after: string }>;
    deployed: boolean;
  }> {
    const response = await this.api.patch(`/apps/${id}/parameters`, data);
    return response.data;
  }

  async getAppParameterHistory(
    id: string,
    limit = 50,
  ): Promise<
    Array<{
      id: string;
      field: string;
      before: string | null;
      after: string | null;
      userId: string;
      createdAt: string;
      user?: { id: string; email: string };
    }>
  > {
    const response = await this.api.get(`/apps/${id}/parameters/history`, {
      params: { limit },
    });
    return response.data;
  }

  /** Видалення апки (бекенд сам видаляє воркер на Cloudflare, якщо апка була задеплоєна). */
  async deleteApp(id: string): Promise<void> {
    await this.api.delete(`/apps/${id}`);
  }

  async syncAppEndpoints(
    id: string,
    templateId?: string
  ): Promise<{ message: string; syncedEndpoints: number; endpoints: EndpointMapping[] }> {
    const url = templateId 
      ? `/apps/${id}/sync-endpoints?templateId=${encodeURIComponent(templateId)}`
      : `/apps/${id}/sync-endpoints`;
      const response = await this.api.post(url);
      return response.data;
  }

  // Templates methods
  async getTemplates(): Promise<Template[]> {
    const response = await this.api.get("/templates");
    return response.data?.templates || response.data || [];
  }

  async getTemplatesAllVersions(): Promise<Template[]> {
    const response = await this.api.get("/templates/all-versions");
    return response.data?.templates || response.data || [];
  }

  async getTemplate(id: string): Promise<Template> {
    const response = await this.api.get(`/templates/${id}`);
    return response.data;
  }

	async getTemplateDynamicFields(id: string, appId?: string): Promise<TemplateDynamicFields> {
		const params = appId ? { appId } : {};
    const response = await this.api.get(`/templates/${id}/dynamic-fields`, {params});
    return response.data;
  }

  async validateTemplateCode(code: string): Promise<{
    isValid: boolean;
    errors: string[];
    detailedErrors?: Array<{
      message: string;
      line?: number;
      column?: number;
      type: 'syntax' | 'macro' | 'macro-comment' | 'security';
    }>;
    exports: Array<{ name: string; description?: string }>;
    endpoints: Array<{
      path: string;
      method: string;
      handler: string;
      description?: string;
      required: boolean;
    }>;
    macros: Array<{
      name: string;
      type: string;
      description?: string;
      defaultValue?: string;
    }>;
  }> {
    const response = await this.api.post("/templates/validate-code", { code });
    return response.data;
  }

  async createTemplate(data: Partial<Template>): Promise<Template> {
    const response = await this.api.post("/templates", data);
    return response.data;
  }

  async updateTemplate(id: string, data: Partial<Template>): Promise<Template> {
    const response = await this.api.patch(`/templates/${id}`, data);
    return response.data;
  }

  async deleteTemplate(id: string): Promise<void> {
    await this.api.delete(`/templates/${id}`);
  }

  // Template versions methods
  async getTemplateVersions(templateId: string): Promise<any> {
    const response = await this.api.get(`/templates/${templateId}/versions`);
    return response.data;
  }

  async getAllTemplateVersions(): Promise<any> {
    const response = await this.api.get("/templates/versions");
    return response.data;
  }

  async rollbackTemplateVersion(
    templateId: string,
    versionId: string
  ): Promise<any> {
    const response = await this.api.post(`/templates/${templateId}/rollback`, {
      versionId,
    });
    return response.data;
  }

  async getTemplateVersionStatus(templateId: string): Promise<any> {
    const response = await this.api.get(
      `/templates/${templateId}/version-status`
    );
    return response.data;
  }

  // Cloudflare endpoints
  /** Повний синк зон з Cloudflare (~48 сторінок + перезапис таблиці) може тривати довше за дефолтні 30 с. */
  private static readonly ZONES_SYNC_TIMEOUT_MS = 120_000;

  async getCloudflareZones(): Promise<any[]> {
    // Якщо кеш доменів порожній, бекенд синхронізує з Cloudflare прямо в цьому запиті
    const response = await this.api.get("/cloudflare/zones", {
      timeout: ApiService.ZONES_SYNC_TIMEOUT_MS,
    });
    return response.data?.zones || [];
  }

  async refreshCloudflareZones(): Promise<any[]> {
    const response = await this.api.post("/cloudflare/zones/refresh", undefined, {
      timeout: ApiService.ZONES_SYNC_TIMEOUT_MS,
    });
    return response.data?.zones || [];
  }

  async testCloudflareConnection(): Promise<{
    connected: boolean;
    message: string;
  }> {
    const response = await this.api.get("/cloudflare/test-connection");
    return response.data;
  }

  // App deployment
  async deployApp(appId: string, confirmed: boolean = true): Promise<any> {
    const response = await this.api.post(`/apps/${appId}/deploy`, {
      confirmed,
    });
    return response.data;
  }

  /** Скинути кеш LinkFactory для апки (за domain). Доступно USER для своїх апок (по CRM-скоупу). */
  async purgeLfCache(
    appId: string,
    body?: { caching_key?: string; id?: string; max_keys?: number },
  ): Promise<{
    success: boolean;
    domain: string;
    pattern: string;
    matched: number;
    deleted: number;
  }> {
    const response = await this.api.post(
      `/apps/${appId}/purge-lf-cache`,
      body ?? {},
    );
    return response.data;
  }

  /** Масовий деплой (redeploy) кількох апок. Ліміт: до 150 appIds за запит. */
  async deployBulk(body: { appIds: string[]; confirmed?: boolean }): Promise<DeployBulkResult> {
    const response = await this.api.post<DeployBulkResult>("/apps/deploy-bulk", {
      appIds: body.appIds,
      confirmed: body.confirmed ?? true,
    });
    return response.data;
  }

  /** Масове видалення апок. Ліміт: до 150 appIds за запит. */
  async deleteBulk(body: { appIds: string[]; confirmed?: boolean }): Promise<DeleteBulkResult> {
    const response = await this.api.post<DeleteBulkResult>("/apps/delete-bulk", {
      appIds: body.appIds,
      confirmed: body.confirmed ?? true,
    });
    return response.data;
  }

  async getAppLogs(
    appId: string,
    limit?: number,
    since?: string
  ): Promise<any> {
    const params: any = {};
    if (limit) params.limit = limit;
    if (since) params.since = since;

    const response = await this.api.get(`/apps/${appId}/logs`, { params });
    return response.data?.logs || [];
  }

  // TODO: Додати метод видалення логів коли backend буде готовий
  // async deleteAppLogs(appId: string, logIds: string[]): Promise<any> {
  //   const response = await this.api.delete(`/apps/${appId}/logs`, { data: { logIds } });
  //   return response.data;
  // }

  async getWorkerCode(appId: string): Promise<{ code: string }> {
    const response = await this.api.get(`/apps/${appId}/worker-code`);
    return response.data;
  }

  // ===== LANDINGS API METHODS =====

  // Landing Templates
  async getLandingTemplates(): Promise<LandingTemplate[]> {
    const response = await this.api.get("/landings/templates");
    return response.data;
  }

  async getLandingTemplate(id: string): Promise<LandingTemplate> {
    const response = await this.api.get(`/landings/templates/${id}`);
    return response.data;
  }

  async createLandingTemplate(data: {
    name: string;
    description?: string;
    version?: string;
    category?: string;
    macros?: any[];
    publicEndpoints?: any[];
  }): Promise<LandingTemplate> {
    console.log("📤 Creating landing template:", data);
    const response = await this.api.post("/landings/templates", data);
    return response.data;
  }

  async updateLandingTemplate(
    id: string,
    data: {
      name?: string;
      description?: string;
      version?: string;
      category?: string;
      macros?: any[];
      publicEndpoints?: any[];
    }
  ): Promise<LandingTemplate> {
    console.log("📤 Updating landing template:", id, data);
    const response = await this.api.put(`/landings/templates/${id}`, data);
    return response.data;
  }

  async deleteLandingTemplate(id: string): Promise<void> {
    await this.api.delete(`/landings/templates/${id}`);
  }

  async cloneLandingTemplate(
    id: string,
    name: string
  ): Promise<LandingTemplate> {
    const response = await this.api.post(`/landings/templates/${id}/clone`, {
      name,
    });
    return response.data;
  }

  // Static Sites
  async getStaticSites(appId?: string): Promise<StaticSite[]> {
    const params = appId ? { appId } : {};
    const response = await this.api.get("/landings/static-sites", { params });
    return response.data;
  }

  async getStaticSite(id: string): Promise<StaticSite> {
    const response = await this.api.get(`/landings/static-sites/${id}`);
    return response.data;
  }

  async createStaticSite(data: CreateStaticSitePayload): Promise<StaticSite> {
    const response = await this.api.post("/landings/static-sites", data);
    return response.data;
  }

  async updateStaticSite(id: string, data: any): Promise<StaticSite> {
    const response = await this.api.put(`/landings/static-sites/${id}`, data);
    return response.data;
  }

  async deleteStaticSite(id: string): Promise<void> {
    await this.api.delete(`/landings/static-sites/${id}`);
  }

  // Landing Files
  async getLandingFiles(
    templateId: string,
    parentPath?: string
  ): Promise<LandingFile[]> {
    const params = parentPath ? { parentPath } : {};
    const response = await this.api.get(
      `/landings/templates/${templateId}/files`,
      { params }
    );
    return response.data;
  }

  // Отримати ВСІ файли для файлового менеджера (включаючи вкладені)
  async getAllLandingFiles(templateId: string): Promise<LandingFile[]> {
    const response = await this.api.get(
      `/landings/templates/${templateId}/files/all`
    );
    return response.data;
  }

  async uploadLandingFile(
    templateId: string,
    file: File,
    filePath: string,
    parentPath?: string
  ): Promise<LandingFile> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("filePath", filePath);
    if (parentPath) {
      formData.append("parentPath", parentPath);
    }

    const response = await this.api.post(
      `/landings/templates/${templateId}/files`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return response.data;
  }

  async createLandingDirectory(
    templateId: string,
    dirName: string,
    dirPath: string,
    parentPath?: string
  ): Promise<LandingFile> {
    const response = await this.api.post(
      `/landings/templates/${templateId}/directories`,
      {
        dirName,
        dirPath,
        parentPath,
      }
    );
    return response.data;
  }

  async getLandingFileContent(fileId: string): Promise<Blob> {
    const response = await this.api.get(`/landings/files/${fileId}`, {
      responseType: "blob",
    });
    return response.data;
  }

  async updateLandingFileContent(
    fileId: string,
    content: string,
    contentType?: string
  ): Promise<LandingFile> {
    const response = await this.api.put(`/landings/files/${fileId}/content`, {
      content,
      contentType,
    });
    return response.data;
  }

  async deleteLandingFile(fileId: string): Promise<void> {
    await this.api.delete(`/landings/files/${fileId}`);
  }

  // ==== Файли домену апки (віддаються воркером у рантаймі) ====
  async getDomainFiles(appId: string): Promise<AppDomainFile[]> {
    const response = await this.api.get(`/apps/${appId}/domain-files`);
    return response.data;
  }

  async uploadDomainFile(
    appId: string,
    file: File,
    path: string
  ): Promise<AppDomainFile> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("path", path);
    const response = await this.api.post(
      `/apps/${appId}/domain-files`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  }

  async getDomainFileContent(appId: string, fileId: string): Promise<Blob> {
    const response = await this.api.get(
      `/apps/${appId}/domain-files/${fileId}/content`,
      { responseType: "blob" }
    );
    return response.data;
  }

  async deleteDomainFile(appId: string, fileId: string): Promise<void> {
    await this.api.delete(`/apps/${appId}/domain-files/${fileId}`);
  }

  // Перейменування файлу або папки
  async renameLandingFile(
    fileId: string,
    newName: string
  ): Promise<LandingFile> {
    const response = await this.api.put(`/landings/files/${fileId}/rename`, {
      newName,
    });
    return response.data;
  }

  // Копіювання файлів
  async copyLandingFiles(
    fileIds: string[],
    destinationPath: string,
    templateId: string
  ): Promise<LandingFile[]> {
    const response = await this.api.put(
      `/landings/templates/${templateId}/files/copy`,
      {
        fileIds,
        destinationPath,
      }
    );
    return response.data;
  }

  // Переміщення файлів
  async moveLandingFiles(
    fileIds: string[],
    destinationPath: string,
    templateId: string
  ): Promise<LandingFile[]> {
    const response = await this.api.put(
      `/landings/templates/${templateId}/files/move`,
      {
        fileIds,
        destinationPath,
      }
    );
    return response.data;
  }

  // Static File Resolution (for testing/preview)
  async resolveStaticFile(
    basePath: string,
    filePath: string,
    appId?: string,
    subdomain?: string
  ): Promise<{
    site: StaticSite;
    file: LandingFile | null;
    resolvedPath: string;
    downloadUrl?: string;
  }> {
    const params: any = {};
    if (appId) params.appId = appId;
    if (subdomain) params.subdomain = subdomain;

    const response = await this.api.get(
      `/landings/resolve/${basePath}/${filePath}`,
      { params }
    );
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService; 